// @tier: PREMIUM — Gestion des demandes parents
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdownMenu';
import { StatusBadge } from '../components/ui/StatusBadge';
import { createStaticMapUrl } from '../config/mapboxConfig';
import {
  Search,
  Filter,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  MessageSquare,
  Calendar,
  MapPin,
  Users,
  Loader2,
  CalendarOff,
  ArrowLeftRight,
  MoreHorizontal,
  Inbox,
  GraduationCap,
} from 'lucide-react';

import useRequests from '../hooks/trRequests';

const isAbsence = (request) => request.type === 'absence';

// Une absence est auto-validée à la soumission (pas de workflow
// pending/approved/rejected admin) — status reste 'pending' en base pour
// toujours. La clé 'declared' (StatusBadge) affiche "Déclarée" au lieu de
// "En attente", qui laisserait croire à tort qu'elle est en cours d'examen
// (cf. même choix déjà fait côté app parent, écran "Mes demandes").
const statusKeyFor = (request) => (isAbsence(request) ? 'declared' : request.status);

const getTypeLabel = (request) =>
  isAbsence(request) ? (
    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
      <CalendarOff className="h-3 w-3" /> Absence
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
      <ArrowLeftRight className="h-3 w-3" /> Transport
    </span>
  );

const getStudentName = (request) =>
  `${request.students?.first_name || ''} ${request.students?.last_name || ''}`.trim() || '—';
const getParentName = (request) =>
  `${request.parents?.first_name || ''} ${request.parents?.last_name || ''}`.trim() || '—';

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('fr-FR') : '—');

// Une absence peut couvrir plusieurs jours consécutifs (start_date/end_date) —
// affiche une date unique si l'absence ne dure qu'un jour, sinon la plage.
const formatAbsenceRange = (request) => {
  if (!request.start_date) return '—';
  if (!request.end_date || request.end_date === request.start_date) {
    return formatDate(request.start_date);
  }
  return `Du ${formatDate(request.start_date)} au ${formatDate(request.end_date)}`;
};

const RequestsManagementPremium = () => {
  const {
    filteredRequests,
    stats,
    loading,
    loadData,
    searchTerm,
    setSearchTerm,
    filterStatus,
    setFilterStatus,
    selectedRequest,
    showDetailDialog,
    onDetailDialogOpenChange,
    showActionDialog,
    onActionDialogOpenChange,
    actionType,
    actionNote,
    setActionNote,
    nearbyStops,
    loadingNearbyStops,
    handleViewDetails,
    handleApprove,
    handleReject,
    confirmAction,
    pagination,
    goToPage,
  } = useRequests();

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Demandes</h1>
          <p className="text-gray-600 mt-1">Demandes de changement de transport soumises par les parents</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Inbox, bg: 'bg-blue-100', text: 'text-blue-600', label: 'Total', value: stats.total },
          { icon: Clock, bg: 'bg-orange-100', text: 'text-orange-600', label: 'En Attente', value: stats.pending },
          {
            icon: CheckCircle,
            bg: 'bg-green-100',
            text: 'text-green-600',
            label: 'Approuvées',
            value: stats.approved,
          },
          { icon: XCircle, bg: 'bg-red-100', text: 'text-red-600', label: 'Refusées', value: stats.rejected },
        ].map(({ icon: Icon, bg, text, label, value }) => (
          <div
            key={label}
            className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex items-center space-x-4"
          >
            <div className={`p-3 ${bg} rounded-lg ${text}`}>
              <Icon size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">{label}</p>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filtres */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher par nom d'élève ou de parent..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtres:</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { value: 'all', label: 'Tous', active: 'bg-blue-100 text-blue-800' },
                { value: 'pending', label: 'En attente', active: 'bg-yellow-100 text-yellow-800' },
                { value: 'approved', label: 'Approuvées', active: 'bg-green-100 text-green-800' },
                { value: 'rejected', label: 'Refusées', active: 'bg-red-100 text-red-800' },
              ].map(({ value, label, active }) => (
                <button
                  key={value}
                  onClick={() => setFilterStatus(value)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    filterStatus === value ? active : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={loadData}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualiser"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Vue cartes — mobile uniquement */}
        <div className="md:hidden divide-y divide-gray-100">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Chargement...</div>
          ) : filteredRequests.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Aucune demande trouvée</div>
          ) : (
            filteredRequests.map((request) => (
              <div key={request.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{getStudentName(request)}</p>
                    <p className="text-xs text-gray-500">{getParentName(request)}</p>
                  </div>
                  <StatusBadge status={statusKeyFor(request)} />
                </div>
                <div className="mt-2 ml-0 text-xs text-gray-500 flex items-center gap-3">
                  {getTypeLabel(request)}
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {formatDate(request.created_at)}
                  </span>
                </div>
                <div className="mt-3 flex justify-end">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        aria-label="Actions"
                        className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                      >
                        <MoreHorizontal size={16} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem onClick={() => handleViewDetails(request)}>
                        <Eye size={14} className="mr-2" />
                        Voir les détails
                      </DropdownMenuItem>
                      {request.status === 'pending' && !isAbsence(request) && (
                        <>
                          <DropdownMenuItem
                            onClick={() => handleApprove(request)}
                            className="text-green-600 focus:text-green-600 focus:bg-green-50"
                          >
                            <CheckCircle size={14} className="mr-2" />
                            Approuver
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleReject(request)}
                            className="text-red-600 focus:text-red-600 focus:bg-red-50"
                          >
                            <XCircle size={14} className="mr-2" />
                            Refuser
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Tableau — desktop uniquement */}
        <div className="overflow-x-auto hidden md:block">
          <table className="min-w-[780px] w-full table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Élève
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Parent
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date Création
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-4 py-3 w-28 whitespace-nowrap text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    Chargement...
                  </td>
                </tr>
              ) : filteredRequests.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-1">Aucune demande trouvée</h3>
                    <p className="text-sm text-gray-500">
                      {searchTerm || filterStatus !== 'all'
                        ? 'Aucune demande ne correspond aux critères de recherche.'
                        : "Aucune demande n'a encore été soumise par un parent."}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                          <GraduationCap className="text-blue-600" size={20} />
                        </div>
                        <div className="ml-4 min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate">{getStudentName(request)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">{getParentName(request)}</td>
                    <td className="px-4 py-4">{getTypeLabel(request)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(request.created_at)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <StatusBadge status={statusKeyFor(request)} />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              aria-label="Actions"
                              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                            >
                              <MoreHorizontal size={16} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem onClick={() => handleViewDetails(request)}>
                              <Eye size={14} className="mr-2" />
                              Voir les détails
                            </DropdownMenuItem>
                            {request.status === 'pending' && !isAbsence(request) && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => handleApprove(request)}
                                  className="text-green-600 focus:text-green-600 focus:bg-green-50"
                                >
                                  <CheckCircle size={14} className="mr-2" />
                                  Approuver
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleReject(request)}
                                  className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                >
                                  <XCircle size={14} className="mr-2" />
                                  Refuser
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {!loading && pagination.pages > 1 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-1">
          <p className="text-sm text-gray-600">
            Page {pagination.page} sur {pagination.pages} — {pagination.total} demande{pagination.total > 1 ? 's' : ''}{' '}
            au total
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => goToPage(pagination.page - 1)}
            >
              Précédent
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= pagination.pages}
              onClick={() => goToPage(pagination.page + 1)}
            >
              Suivant
            </Button>
          </div>
        </div>
      )}

      {/* Détails */}
      <Dialog open={showDetailDialog} onOpenChange={onDetailDialogOpenChange}>
        <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-6xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Détails de la Demande</DialogTitle>
            <DialogDescription>
              {selectedRequest && isAbsence(selectedRequest) ? 'Absence' : 'Changement de transport'} —{' '}
              {selectedRequest && getStudentName(selectedRequest)}
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-6 overflow-y-auto pr-1 -mr-1 flex-1 min-h-0">
              {/* Ligne 1 — informations texte + dates */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Élève</label>
                  <p className="mt-1 font-medium">{getStudentName(selectedRequest)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Parent</label>
                  <p className="mt-1 font-medium">{getParentName(selectedRequest)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Statut</label>
                  <div className="mt-1">
                    <StatusBadge status={statusKeyFor(selectedRequest)} />
                  </div>
                </div>
                {isAbsence(selectedRequest) ? (
                  <div>
                    <label className="text-sm font-medium text-gray-600 flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      Dates d'absence
                    </label>
                    <p className="mt-1">{formatAbsenceRange(selectedRequest)}</p>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Type</label>
                      <p className="mt-1">
                        {selectedRequest.is_temporary ? 'Jour précis (exceptionnel)' : 'Changement définitif'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600 flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {selectedRequest.is_temporary ? 'Date' : 'À partir du'}
                      </label>
                      <p className="mt-1">{formatDate(selectedRequest.effective_date)}</p>
                    </div>
                    {selectedRequest.is_temporary && (
                      <div>
                        <label className="text-sm font-medium text-gray-600">Créneau</label>
                        <p className="mt-1">
                          {{ morning: 'Matin', evening: 'Soir', both: 'Matin et soir' }[selectedRequest.trip_type] ||
                            '—'}
                        </p>
                      </div>
                    )}
                    {selectedRequest.accompanied_by && (
                      <div>
                        <label className="text-sm font-medium text-gray-600 flex items-center">
                          <Users className="h-4 w-4 mr-1" />
                          Déposé / Récupéré par
                        </label>
                        <p className="mt-1">{selectedRequest.accompanied_by}</p>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600 flex items-center">
                  <MessageSquare className="h-4 w-4 mr-1" />
                  Motif
                </label>
                <p className="mt-1 p-3 bg-gray-50 rounded-lg">{selectedRequest.reason}</p>
              </div>

              {/* Ligne 2 — carte et arrêts à proximité, côte à côte sur desktop */}
              {!isAbsence(selectedRequest) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600 flex items-center">
                      <MapPin className="h-4 w-4 mr-1" />
                      Emplacement souhaité
                    </label>
                    {/* proposed_address n'est jamais renseigné : le parent dépose un pin sur
                        la carte (minimisation des données, CNDP — voir docs/ideas/parent-requests.md),
                        aucune saisie d'adresse côté app. Aperçu carte + coordonnées à la place
                        d'un simple "—" pour que l'admin sache où se trouve la demande. */}
                    {selectedRequest.proposed_latitude != null && selectedRequest.proposed_longitude != null ? (
                      <div className="mt-2 space-y-2">
                        <img
                          src={createStaticMapUrl(
                            {
                              lat: parseFloat(selectedRequest.proposed_latitude),
                              lng: parseFloat(selectedRequest.proposed_longitude),
                            },
                            15,
                            '600x300',
                            'streets-v12',
                            { marker: true, markerColor: '7c3aed', hideAttribution: true, hideLogo: true },
                          )}
                          alt="Emplacement proposé par le parent"
                          className="w-full rounded-lg border border-gray-200"
                        />
                        <p className="text-sm text-gray-600">
                          {selectedRequest.proposed_address ||
                            `${parseFloat(selectedRequest.proposed_latitude).toFixed(6)}, ${parseFloat(selectedRequest.proposed_longitude).toFixed(6)}`}
                        </p>
                      </div>
                    ) : (
                      <p className="mt-1">—</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600 flex items-center">
                      <MapPin className="h-4 w-4 mr-1" />
                      Arrêts à proximité
                    </label>
                    {/* Purement informatif pour l'admin — aucune sélection possible ici,
                        l'approbation ne conditionne rien sur ces arrêts (voir CLAUDE.md). */}
                    {loadingNearbyStops ? (
                      <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                        <Loader2 className="h-4 w-4 animate-spin" /> Recherche des arrêts à proximité...
                      </div>
                    ) : nearbyStops.length === 0 ? (
                      <p className="mt-1 text-sm text-gray-500">Aucun arrêt existant à proximité.</p>
                    ) : (
                      <div className="mt-2 space-y-2 max-h-72 overflow-y-auto">
                        {nearbyStops.map((stop) => (
                          <div
                            key={stop.routeStopId}
                            className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                          >
                            <div>
                              <p className="font-medium text-sm">{stop.stopName}</p>
                              <p className="text-xs text-gray-500">{stop.address}</p>
                            </div>
                            <div className="text-right text-xs">
                              <p className="font-medium">{stop.distanceM} m</p>
                              {stop.sameCircuit ? (
                                <span className="text-green-600">Même circuit</span>
                              ) : (
                                <span className="text-orange-600">Autre circuit</span>
                              )}
                              {stop.busOccupancy?.capacity != null && (
                                <p className="text-gray-400">
                                  {stop.busOccupancy.current}/{stop.busOccupancy.capacity} places
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!isAbsence(selectedRequest) && selectedRequest.status !== 'pending' && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Traitement</h4>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      {selectedRequest.status === 'approved' ? 'Approuvée' : 'Refusée'} le{' '}
                      {formatDate(selectedRequest.processed_at)}
                    </p>
                    {selectedRequest.admin_note && (
                      <p className="mt-1 p-3 bg-gray-50 rounded-lg text-sm">{selectedRequest.admin_note}</p>
                    )}
                    {selectedRequest.reject_reason && (
                      <p className="mt-1 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                        {selectedRequest.reject_reason}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => onDetailDialogOpenChange(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approbation / refus */}
      <Dialog open={showActionDialog} onOpenChange={onActionDialogOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{actionType === 'approve' ? 'Approuver la demande' : 'Refuser la demande'}</DialogTitle>
            <DialogDescription>
              {actionType === 'approve'
                ? 'Le parent sera notifié de cette approbation.'
                : 'Veuillez indiquer la raison du refus. Le parent sera notifié.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">
                {actionType === 'approve' ? 'Note (optionnel)' : 'Raison du refus'}
              </label>
              <Textarea
                placeholder={actionType === 'approve' ? 'Ajouter une note...' : 'Expliquez la raison du refus...'}
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                className="mt-2"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onActionDialogOpenChange(false)}>
              Annuler
            </Button>
            <Button variant={actionType === 'approve' ? 'default' : 'destructive'} onClick={confirmAction}>
              {actionType === 'approve' ? 'Approuver' : 'Refuser'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequestsManagementPremium;
